import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";

import { isNonEmptyString } from "../../utils/reactNode";
import { useDevWarnOnce } from "../../utils/useDevWarnOnce";
import {
  inlineOverlayZIndex,
  resolveFloatingPopoverPosition,
  ESCAPE_LAYER_ATTR,
  escapeLayerValue,
  type FloatingPopoverPlacement,
  type FloatingPopoverPosition,
  type FloatingPopoverRect,
} from "../foundations/popover";
import { usePopoverDismiss } from "../foundations/popoverDismiss";
import { yScrollbarStyles } from "../foundations/scrollbar";
import { useAnchorHiddenClose } from "./useAnchorHiddenClose";
import { useAnchoredReposition } from "./useAnchoredReposition";
// 모듈 직접 경로 — index 경유 시 Modal.tsx를 끌어와 결합/순환 위험.
import {
  topmostModalEl,
  topmostModalMountNode,
  modalMountNode,
  releaseInert,
  MODAL_BACKDROP_ATTR,
  MODAL_EXITING_ATTR,
} from "../Modal/modalStack";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type PopupDescendant = { ref: RefObject<HTMLDivElement | null>; consumesEscape: boolean };
type RegisterPopupDescendant = (entry: PopupDescendant) => () => void;
const PopupOwnerContext = createContext<{
  register: RegisterPopupDescendant;
  position: FloatingPopoverPosition;
} | null>(null);

export type AnchoredPopupAnchor =
  // `el` 있으면 스크롤/리사이즈마다 재측정해 앵커를 따라감(아래 toRect). 없으면 `rect` 스냅샷 고정
  // (수동 anchor 하위호환). `el`은 `openFromElement`가 자동 채움.
  | { type: "rect"; rect: DOMRectReadOnly; el?: HTMLElement | null }
  | { type: "point"; x: number; y: number };

export type AnchoredPopupAutoFocus = "panel" | "firstFocusable" | false;
// "presentation" = 시맨틱 없는 컨테이너 — select/combobox처럼 내부 자식(listbox 등)이 role을
// 소유해 Panel 자체는 role을 노출하지 않아야 할 때(트리거의 aria-haspopup/controls가 그 자식을 가리킴).
export type AnchoredPopupRole = "dialog" | "menu" | "presentation";
export type AnchoredPopupWidth = "matchAnchor" | (string & {});
export type AnchoredPopupSectionPadding = "none" | "sm" | "md";
export type AnchoredPopupFooterAlign = "start" | "center" | "end" | "between";

export interface AnchoredPopupProps {
  open: boolean;
  anchor: AnchoredPopupAnchor | null;
  onClose: () => void;
  children: ReactNode;
  placement?: FloatingPopoverPlacement;
  /** 생략/null은 viewport. DOM 경계 또는 anchor의 clipping 조상을 화면과 교차해 사용한다. Portal host는 바꾸지 않는다. */
  boundary?: "viewport" | "clippingAncestors" | HTMLElement | null;
  /** 주 방향에 공간이 없을 때 검사할 추가 방향. 생략하면 같은 축 안에서만 flip한다. */
  fallbackPlacements?: readonly FloatingPopoverPlacement[];
  /** 이 요소들을 margin만큼 피한다. 모든 후보가 겹치면 popup만 숨긴다. 상호 회피는 지정하지 않는다. */
  avoidElements?: readonly HTMLElement[];
  offset?: number;
  margin?: number;
  /**
   * CSS width, or `"matchAnchor"` to follow a rectangular anchor's border-box width.
   * Point anchors have no width and fall back to `auto`.
   */
  width?: AnchoredPopupWidth;
  maxWidth?: string;
  maxHeight?: string;
  closeOnEscape?: boolean;
  /** panel과 rect 앵커의 `el`(트리거) 밖 pointerdown에 `onClose`. 트리거 재클릭 토글은 소비처 click이 맡는다. */
  closeOnOutsidePointerDown?: boolean;
  /**
   * 열릴 때 focus 이동. 미지정 기본 — 모달 안이면 `role="dialog"`는 `"firstFocusable"`(layered dialog라
   * 키보드 도달 순서 자연스럽게), `role="menu"`는 `"panel"`(arrow-nav 모델이 없어 첫 menuitem focus는 부조화 —
   * 실제 menu 시맨틱은 `<Menu>`가 소유). 그 외(page-level)는 `false`. 명시하면 그 값.
   */
  autoFocus?: AnchoredPopupAutoFocus;
  /** 닫힐 때 직전 focus(보통 트리거)로 복귀. 미지정 기본 — 모달 안이면 `true`, 그 외는 `false`. */
  returnFocus?: boolean;
  repositionOnScroll?: boolean;
  repositionOnResize?: boolean;
  /**
   * rect 앵커의 element가 DOM에서 분리되거나 scroll/resize로 viewport나 overflow 조상을 완전히
   * 벗어나면 다음 프레임에 `onClose`. 소비처가 그 사이 다른 element로 재앵커하면 열린 채 따라간다. 앵커 없이 떠 있는 popup은 무엇에 붙은 레이어인지 알 수 없고,
   * 보이지 않는 panel 안에 focus가 남는다. 경계에 걸친 앵커는 보이는 것으로 본다. point/수동 rect
   * 앵커는 대상이 아니다. 기본 `true`.
   */
  closeOnAnchorHidden?: boolean;
  role?: AnchoredPopupRole;
  /**
   * mount node override(이름은 생태계 관례 유지) — popup root를 append할 노드. 기본은 자기를 띄운 topmost
   * 모달의 Dialog(콘텐츠 위로), 모달 없으면 `document.body`. 명시하면 그 노드 안으로. open 시점 1회 캡처.
   */
  container?: HTMLElement | null;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** The panel exposes data-placement with the resolved (flipped) side for attached surface styles. */
  className?: string;
  style?: CSSProperties;
}

export interface UseAnchoredPopupAnchorApi {
  anchor: AnchoredPopupAnchor | null;
  open: boolean;
  setAnchor: (anchor: SetStateAction<AnchoredPopupAnchor | null>) => void;
  close: () => void;
  openFromElement: (event: ReactMouseEvent<HTMLElement>) => void;
  openFromPoint: (event: ReactMouseEvent<HTMLElement>) => void;
  openFromContextMenu: (event: ReactMouseEvent<HTMLElement>) => void;
}

export interface AnchoredPopupSectionProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  padding?: AnchoredPopupSectionPadding;
}

export interface AnchoredPopupHeaderProps extends AnchoredPopupSectionProps {
  divider?: boolean;
}

export interface AnchoredPopupBodyProps extends AnchoredPopupSectionProps {
  scroll?: boolean;
}

export interface AnchoredPopupFooterProps extends AnchoredPopupSectionProps {
  divider?: boolean;
  align?: AnchoredPopupFooterAlign;
}

const SECTION_PADDING: Record<AnchoredPopupSectionPadding, string> = {
  none: "0",
  sm: "0.5rem 0.65rem",
  md: "0.75rem 0.85rem",
};

const FOOTER_ALIGN: Record<AnchoredPopupFooterAlign, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
};

const Layer = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${inlineOverlayZIndex.popover};
  /* 덮개 — 포인터는 통과(아래 클릭 보존). 실제 대상은 Panel. 안 통과시키면 뒤 클릭이 한 번 삼켜진다. */
  pointer-events: none;
`;

const MATCH_ANCHOR_WIDTH_CSS_VAR = "--c6-anchored-popup-anchor-width";

const Panel = styled.div<{
  $width?: string;
  $matchAnchorWidth: boolean;
  $bounded: boolean;
  $maxWidth?: string;
  $maxHeight?: string;
}>`
  position: fixed;
  display: flex;
  flex-direction: column;
  ${({ $matchAnchorWidth, $bounded }) =>
    $matchAnchorWidth || $bounded
      ? css`
          box-sizing: border-box;
        `
      : ""}
  width: ${({ $matchAnchorWidth, $width }) =>
    $matchAnchorWidth ? `var(${MATCH_ANCHOR_WIDTH_CSS_VAR}, auto)` : ($width ?? "auto")};
  max-width: var(
    --c6-popup-boundary-width,
    ${({ $maxWidth }) => $maxWidth ?? "min(28rem, calc(100vw - 1rem))"}
  );
  max-height: var(
    --c6-popup-boundary-height,
    ${({ $maxHeight }) => $maxHeight ?? "calc(100vh - 1rem)"}
  );
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
  color: ${({ theme }) => theme.color.text.default};
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  box-shadow: ${({ theme }) => theme.elevation.shadow.overlay};
  outline: none;
  /* Layer가 pointer-events:none이라 실제 상호작용 대상인 Panel에서 다시 켠다. */
  pointer-events: auto;
`;

const HeaderRoot = styled.div<{
  $padding: AnchoredPopupSectionPadding;
  $divider: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: ${({ $padding }) => SECTION_PADDING[$padding]};
  ${({ $divider, theme }) =>
    $divider
      ? css`
          border-bottom: 1px solid ${theme.color.border.subtle};
        `
      : ""}
`;

const BodyRoot = styled.div<{
  $padding: AnchoredPopupSectionPadding;
  $scroll: boolean;
}>`
  min-height: 0;
  padding: ${({ $padding }) => SECTION_PADDING[$padding]};
  ${({ $scroll }) => ($scroll ? yScrollbarStyles : "")}
`;

const FooterRoot = styled.div<{
  $align: AnchoredPopupFooterAlign;
  $padding: AnchoredPopupSectionPadding;
  $divider: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => FOOTER_ALIGN[$align]};
  gap: 0.4rem;
  padding: ${({ $padding }) => SECTION_PADDING[$padding]};
  ${({ $divider, theme }) =>
    $divider
      ? css`
          border-top: 1px solid ${theme.color.border.subtle};
        `
      : ""}
`;

function toRect(anchor: AnchoredPopupAnchor): FloatingPopoverRect {
  if (anchor.type === "rect") {
    // el이 살아 있으면 매번 재측정 — 스냅샷만 쓰면 스크롤 시 popup이 앵커에서 떨어진다. 분리/미지정이면 스냅샷.
    return anchor.el?.isConnected ? anchor.el.getBoundingClientRect() : anchor.rect;
  }

  return {
    top: anchor.y,
    right: anchor.x,
    bottom: anchor.y,
    left: anchor.x,
    width: 0,
    height: 0,
  };
}

function getFirstFocusable(root: HTMLElement): HTMLElement | null {
  return root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
}

function getDefaultPlacement(anchor: AnchoredPopupAnchor | null): FloatingPopoverPlacement {
  return anchor?.type === "point" ? "bottom-start" : "bottom-end";
}

export function useAnchoredPopupAnchor(): UseAnchoredPopupAnchorApi {
  const [anchor, setAnchor] = useState<AnchoredPopupAnchor | null>(null);

  const close = useCallback(() => setAnchor(null), []);

  const openFromElement = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    // el도 보관 → 스크롤/리사이즈 시 재측정으로 앵커 따라감(rect는 el 분리 시 fallback).
    setAnchor({
      type: "rect",
      rect: event.currentTarget.getBoundingClientRect(),
      el: event.currentTarget,
    });
  }, []);

  const openFromPoint = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    setAnchor({ type: "point", x: event.clientX, y: event.clientY });
  }, []);

  const openFromContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      event.preventDefault();
      openFromPoint(event);
    },
    [openFromPoint],
  );

  return {
    anchor,
    open: anchor != null,
    setAnchor,
    close,
    openFromElement,
    openFromPoint,
    openFromContextMenu,
  };
}

function clippingAncestors(element: HTMLElement | null | undefined): HTMLElement[] {
  const result: HTMLElement[] = [];
  for (let parent = element?.parentElement; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (/(auto|scroll|hidden|clip)/.test(`${style.overflowX} ${style.overflowY}`))
      result.push(parent);
  }
  return result;
}

function elementBoundary(element: HTMLElement): FloatingPopoverRect {
  const bounds = element.getBoundingClientRect();
  const left = Math.max(0, Math.min(window.innerWidth, bounds.left));
  const top = Math.max(0, Math.min(window.innerHeight, bounds.top));
  const right = Math.max(left, Math.min(window.innerWidth, bounds.right));
  const bottom = Math.max(top, Math.min(window.innerHeight, bounds.bottom));
  return { left, top, right, bottom, width: right - left, height: bottom - top };
}

function clippingBoundary(element: HTMLElement | null | undefined): FloatingPopoverRect {
  let left = 0,
    top = 0,
    right = window.innerWidth,
    bottom = window.innerHeight;
  for (const ancestor of clippingAncestors(element)) {
    const rect = ancestor.getBoundingClientRect();
    const style = getComputedStyle(ancestor);
    const scaleX = ancestor.offsetWidth ? rect.width / ancestor.offsetWidth : 1;
    const scaleY = ancestor.offsetHeight ? rect.height / ancestor.offsetHeight : 1;
    if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
      const x = rect.left + ancestor.clientLeft * scaleX;
      left = Math.max(left, x);
      right = Math.min(right, x + ancestor.clientWidth * scaleX);
    }
    if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
      const y = rect.top + ancestor.clientTop * scaleY;
      top = Math.max(top, y);
      bottom = Math.min(bottom, y + ancestor.clientHeight * scaleY);
    }
  }
  return {
    left,
    top,
    right: Math.max(left, right),
    bottom: Math.max(top, bottom),
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

const AnchoredPopupRoot = ({
  open,
  anchor,
  onClose,
  children,
  placement,
  boundary = "viewport",
  fallbackPlacements,
  avoidElements,
  offset = 6,
  margin = 8,
  width,
  maxWidth,
  maxHeight,
  closeOnEscape = true,
  closeOnOutsidePointerDown = true,
  // 기본값 없음 — in-modal 여부에 따라 effective 값 해석(아래 open effect / autoFocus layoutEffect).
  autoFocus,
  returnFocus,
  repositionOnScroll = true,
  repositionOnResize = true,
  closeOnAnchorHidden = true,
  role = "dialog",
  container,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  className,
  style,
}: AnchoredPopupProps) => {
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<FloatingPopoverPosition>({
    left: 0,
    top: 0,
    placement: placement ?? getDefaultPlacement(anchor),
  });
  const panelRef = useRef<HTMLDivElement | null>(null);
  const appliedBoundary = useRef(false);
  const appliedAvoidance = useRef(false);
  const restoreFocusToRef = useRef<HTMLElement | null>(null);
  // 이번 open이 모달 Dialog로 mount됐나(=layered dialog) — autoFocus/returnFocus 기본값 해석용.
  const intoModalRef = useRef(false);
  const owner = useContext(PopupOwnerContext);
  const registerWithOwner = owner?.register;
  const [descendants, setDescendants] = useState<readonly PopupDescendant[]>([]);
  const registerDescendant = useCallback<RegisterPopupDescendant>(
    (entry) => {
      setDescendants((current) => [...current, entry]);
      const releaseOwner = registerWithOwner?.(entry);
      return () => {
        setDescendants((current) => current.filter((candidate) => candidate !== entry));
        releaseOwner?.();
      };
    },
    [registerWithOwner],
  );
  const hasAnchor = anchor !== null;
  useLayoutEffect(() => {
    if (!open || !hasAnchor) return;
    return registerWithOwner?.({ ref: panelRef, consumesEscape: closeOnEscape });
  }, [open, hasAnchor, registerWithOwner, closeOnEscape]);
  const descendantContext = useMemo(
    () => ({ register: registerDescendant, position }),
    [registerDescendant, position],
  );
  // anchor 트리거(rect anchor의 el) — popup이 어느 모달 소속인지 판정 + outside-dismiss 경계에 포함.
  // 매 렌더 동기화(포털 재생성 회피).
  const anchorElementRef = useRef<HTMLElement | null>(null);
  anchorElementRef.current = anchor?.type === "rect" ? (anchor.el ?? null) : null;
  // 트리거 pointerdown은 바깥이 아니다 — document dismiss가 먼저 닫고 뒤따르는 트리거 click이 다시 여는
  // 토글 레이스를 트리거 쪽 stopPropagation 없이 막는다. 그래야 닫힌 다른 트리거의 pointerdown이 전파돼
  // 열린 popup을 dismiss하고, 한 번에 하나의 popup만 열린다.
  const dismissRefs = useMemo(
    () => [panelRef, anchorElementRef, ...descendants.map(({ ref }) => ref)],
    [descendants],
  );
  const matchAnchorWidth = width === "matchAnchor";
  const boundaryElement = typeof boundary === "object" ? boundary : null;
  const bounded = boundary === "clippingAncestors" || boundaryElement !== null;

  useDevWarnOnce(
    open && role === "dialog" && !isNonEmptyString(ariaLabel) && !isNonEmptyString(ariaLabelledBy),
    '[c6] <AnchoredPopup role="dialog"> requires an accessible name. Provide `aria-label` or `aria-labelledby`.',
  );

  const updatePosition = useCallback(() => {
    if (!anchor || !panelRef.current) return;
    const anchorRect = toRect(anchor);
    const bounds =
      boundary === "clippingAncestors"
        ? clippingBoundary(anchor.type === "rect" ? anchor.el : null)
        : boundaryElement
          ? elementBoundary(boundaryElement)
          : undefined;
    if (bounds) {
      appliedBoundary.current = true;
      // 키워드/var()를 포함한 소비처 CSS를 먼저 해석한다. 이전 clamp를 지워 경계 확장도 반영한다.
      panelRef.current.style.removeProperty("--c6-popup-boundary-width");
      panelRef.current.style.removeProperty("--c6-popup-boundary-height");
      // 소속 Item이 보드 밖으로 스크롤되면 떠 있는 메뉴만 남기지 않는다. 선택 상태는 소유자가 유지한다.
      panelRef.current.style.visibility =
        boundary !== "clippingAncestors" ||
        (bounds.width > 0 &&
          bounds.height > 0 &&
          anchorRect.right > bounds.left &&
          anchorRect.left < bounds.right &&
          anchorRect.bottom > bounds.top &&
          anchorRect.top < bounds.bottom)
          ? (style?.visibility ?? "")
          : "hidden";
    } else if (appliedBoundary.current) {
      appliedBoundary.current = false;
      panelRef.current.style.removeProperty("--c6-popup-boundary-width");
      panelRef.current.style.removeProperty("--c6-popup-boundary-height");
      panelRef.current.style.visibility = style?.visibility ?? "";
    }
    if (matchAnchorWidth) {
      if (anchor.type === "rect") {
        const nextWidth = `${anchorRect.width}px`;
        if (panelRef.current.style.getPropertyValue(MATCH_ANCHOR_WIDTH_CSS_VAR) !== nextWidth) {
          // Anchor 폭은 resize 중 자주 바뀔 수 있으므로 styled 보간 대신 inline CSS 변수만 갱신한다.
          panelRef.current.style.setProperty(MATCH_ANCHOR_WIDTH_CSS_VAR, nextWidth);
        }
      } else {
        panelRef.current.style.removeProperty(MATCH_ANCHOR_WIDTH_CSS_VAR);
      }
    }
    let rect = panelRef.current.getBoundingClientRect();
    if (bounds) {
      const maxBoundaryWidth = Math.max(0, bounds.width - margin * 2);
      const maxBoundaryHeight = Math.max(0, bounds.height - margin * 2);
      if (rect.width > maxBoundaryWidth) {
        panelRef.current.style.setProperty("--c6-popup-boundary-width", `${maxBoundaryWidth}px`);
        // 폭 제한으로 줄바꿈된 높이에 대해 세로 경계를 검사한다.
        rect = panelRef.current.getBoundingClientRect();
      }
      if (rect.height > maxBoundaryHeight) {
        panelRef.current.style.setProperty("--c6-popup-boundary-height", `${maxBoundaryHeight}px`);
        rect = panelRef.current.getBoundingClientRect();
      }
    }
    const next = resolveFloatingPopoverPosition({
      anchorRect,
      popoverSize: { width: rect.width, height: rect.height },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      placement: placement ?? getDefaultPlacement(anchor),
      offset,
      margin,
      boundary: bounds,
      fallbackPlacements,
      avoidRects: avoidElements
        ?.filter(
          (element) => element.isConnected && getComputedStyle(element).visibility !== "hidden",
        )
        .map((element) => element.getBoundingClientRect()),
    });
    if (avoidElements?.length || appliedAvoidance.current) {
      appliedAvoidance.current = Boolean(avoidElements?.length);
      const outside =
        boundary === "clippingAncestors" &&
        bounds &&
        (bounds.width <= 0 ||
          bounds.height <= 0 ||
          anchorRect.right <= bounds.left ||
          anchorRect.left >= bounds.right ||
          anchorRect.bottom <= bounds.top ||
          anchorRect.top >= bounds.bottom);
      panelRef.current.style.visibility =
        next.hidden || outside ? "hidden" : (style?.visibility ?? "");
    }
    // Keep the resolved placement: attached surfaces style their joined edge from data-placement.
    setPosition(next);
  }, [
    anchor,
    matchAnchorWidth,
    placement,
    offset,
    margin,
    boundary,
    boundaryElement,
    fallbackPlacements,
    avoidElements,
    style?.visibility,
  ]);

  // autoFocus 적용 — 미지정이면 in-modal일 때만 firstFocusable로 진입, 명시값은 존중. relocate 경로에서
  // 재호출되므로 idempotent.
  const focusInitial = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    // in-modal 기본: dialog는 첫 focusable로 진입, menu는 panel로만 진입. AnchoredPopup엔 menu용 arrow-nav/
    // roving 모델이 없어 첫 menuitem에 focus만 주면 "menu로 announce되는데 화살표가 안 먹는" 부조화 + roving
    // (menuitem tabindex=-1)이면 firstFocusable이 못 잡는다. 실제 menu 시맨틱은 <Menu>가 소유(autoFocus 명시).
    const inModalDefault = role === "menu" ? "panel" : "firstFocusable";
    const effectiveAutoFocus = autoFocus ?? (intoModalRef.current ? inModalDefault : false);
    if (effectiveAutoFocus === false) return;
    const target = effectiveAutoFocus === "firstFocusable" ? getFirstFocusable(panel) : panel;
    target?.focus();
  }, [autoFocus, role]);
  // rAF relocate가 최신 focusInitial을 호출하게 ref 보관(open effect를 autoFocus에 의존시키면 변경 시 포털 재생성).
  const focusInitialRef = useRef(focusInitial);
  focusInitialRef.current = focusInitial;
  // 모달이 popup보다 먼저 닫혀 el을 떼어갈 때 호출할 onClose(아래 disconnect observer) — ref 보관(포털 재생성 회피).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // 모달이 el을 disconnect시켜 self-close된 경우 — cleanup에서 returnFocus 스킵(트리거 사라졌고 모달이
  // focus 복원 담당이라 focus를 body로 안 흘림).
  const hostDisconnectedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    hostDisconnectedRef.current = false;

    const el = document.createElement("div");
    el.setAttribute("data-c6-anchored-popup-root", "");
    // 트리거(anchor.el)가 topmost 모달 "밖"이면 이 popup은 그 모달 소속 아님 — 모달 위 높은 z 오버레이
    // (예약 1200 등)에서 연 popup을 모달 Dialog(1100 밴드)로 끌어들이면 트리거 뒤로 가고 모달 실드를 상속하므로
    // 제외. el 없는 anchor(point/수동)는 판정 불가라 in-modal 허용(소비처는 container override 가능).
    const ownerModal = topmostModalEl();
    const anchorOutsideOwner =
      ownerModal != null &&
      anchorElementRef.current != null &&
      !ownerModal.contains(anchorElementRef.current);
    // mount node 우선순위: 명시 override → (소속일 때만) topmost 모달 Dialog → body.
    const mountNode =
      container ?? (anchorOutsideOwner ? null : topmostModalMountNode()) ?? document.body;
    // 모달 안에 mount되나(focus·self-close 기본 결정). 트리 순서상 미확정이면 아래 relocate가 발견 시 승격.
    intoModalRef.current = !container && !anchorOutsideOwner && ownerModal != null;

    // 직전 focus는 항상 저장 — in-modal 여부가 relocate에서 늦게 확정될 수 있음. 복원 여부는 cleanup에서 결정.
    restoreFocusToRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    mountNode.appendChild(el);
    setPortalEl(el);

    let raf = 0;
    let disconnectObserver: MutationObserver | null = null;
    let exitObserver: MutationObserver | null = null;
    // 모달이 popup보다 먼저 닫히면 modalStack의 el.remove()가 Dialog와 함께 이 popup el까지 떼어간다 →
    // React가 끊긴 portalEl에 reconcile하고 returnFocus가 사라진 트리거를 향한다. 모달이 el을 떼어가면 감지해
    // 스스로 닫아 깔끔히 unmount + returnFocus 스킵. page-level은 떼어갈 모달이 없어 불필요.
    const observeHostDisconnect = () => {
      if (disconnectObserver) return;
      disconnectObserver = new MutationObserver(() => {
        if (!el.isConnected) {
          hostDisconnectedRef.current = true;
          onCloseRef.current();
        }
      });
      disconnectObserver.observe(document.body, { childList: true });
    };
    // 모달이 exit(closing) 시작하면(Backdrop에 MODAL_EXITING_ATTR) el disconnect 전에 즉시 self-close한다 —
    // motion 브라우저에선 모달이 MOTION_MS 동안 연결된 채 fade-out하므로, 안 그러면 그 사이 popup이 interactive로
    // 남는다(opacity만 fade, focus/click은 가능). disconnect와 달리 el을 안 떼므로(300ms 안 재-open으로 exit가
    // 취소될 수도) hostDisconnected로 마킹하지 않는다 — 트리거가 살아 있으니 cleanup의 normal returnFocus에
    // 맡긴다(exit 취소 시 focus가 body에 고아로 남지 않게).
    const observeModalExit = () => {
      if (exitObserver) return;
      const backdrop = el.closest(`[${MODAL_BACKDROP_ATTR}]`);
      if (!backdrop) return;
      const closeIfExiting = () => {
        if (!backdrop.hasAttribute(MODAL_EXITING_ATTR)) return;
        onCloseRef.current();
      };
      exitObserver = new MutationObserver(closeIfExiting);
      exitObserver.observe(backdrop, { attributes: true, attributeFilter: [MODAL_EXITING_ATTR] });
      closeIfExiting(); // 등록 시점에 이미 closing이면 즉시
    };
    if (intoModalRef.current) {
      observeHostDisconnect();
      observeModalExit();
    }

    // mountNode가 body로 떨어졌고 명시 container 없으면, same-commit 모달의 Dialog로 옮긴다. popup이 Modal보다
    // 트리 앞이라 effect가 모달 등록 전에 돌아도 첫 프레임엔 모달이 등록돼 있다. 처음 발견 모달을 intendedModal로
    // 고정(이후 무관한 모달로 안 끌려감), 첫 시도에 모달 없으면 genuine page popup → 중단.
    if (!container && mountNode === document.body && !anchorOutsideOwner) {
      let intendedModal: HTMLElement | null = null;
      let tries = 0;
      const relocate = () => {
        if (!intendedModal) {
          intendedModal = topmostModalEl();
          if (!intendedModal) return; // 모달 없음 = genuine page popup
          // 늦게 발견한 모달이라도 트리거가 그 안에 없으면(높은 z 오버레이 등) 소속 아님 → relocate 안 함.
          // el 없는 anchor는 판정 불가라 통과.
          const anchorEl = anchorElementRef.current;
          if (anchorEl != null && !intendedModal.contains(anchorEl)) return;
        }
        const target = modalMountNode(intendedModal);
        if (target) {
          if (el.parentNode !== target) {
            target.appendChild(el);
            intoModalRef.current = true; // 늦게 발견된 모달 → in-modal로 승격
            // body 체류 중 modalStack observer가 이 노드를 inert 처리했을 수 있다. observer는 "추가"에만 반응해
            // body에서 빠져도 자동 해제 안 됨 → 명시 해제(추적 set도 정리). 안 그러면 relocated popup이 모달 위에
            // 보이는데 비활성이 된다.
            releaseInert(el);
            observeHostDisconnect();
            observeModalExit();
            // 첫 autoFocus는 el이 body(inert)에 있어 막혔을 수 있다 → focusable이 된 지금 재시도(idempotent).
            focusInitialRef.current();
          }
          return;
        }
        // ~1s @60fps — 느린 렌더에서도 Dialog commit을 충분히 기다린다. 소진되면 popup은 body(z=20)에
        // 남지만(모달 뒤로 가려짐) cleanup의 releaseInert로 inert는 푼다.
        if (tries++ < 60) raf = requestAnimationFrame(relocate);
      };
      raf = requestAnimationFrame(relocate);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      disconnectObserver?.disconnect();
      exitObserver?.disconnect();
      // relocate가 끝내 실패해 el이 body에 inert로 남았을 수 있다 → 추적까지 정리(removed 노드 잔여 참조 방지).
      releaseInert(el);
      setPortalEl(null);
      el.remove();
      // 바깥 클릭으로 닫히면 클릭한 다른 컨트롤로 focus가 옮겨가 있다 → 그 focus를 트리거로 뺏지 않는다.
      // focus가 body로 떨어진 경우(Escape/프로그램적 닫힘)만 트리거로 복원. preventScroll — 앵커 이탈로
      // 닫힐 때 화면 밖 트리거로 focus가 돌아가면 브라우저가 사용자의 scroll을 되돌린다.
      const active = document.activeElement;
      const focusMovedToAnotherEl =
        active != null && active !== document.body && active.isConnected;
      if (
        (returnFocus ?? intoModalRef.current) &&
        !hostDisconnectedRef.current &&
        !focusMovedToAnotherEl
      ) {
        restoreFocusToRef.current?.focus?.({ preventScroll: true });
      }
      restoreFocusToRef.current = null;
    };
  }, [open, returnFocus, container]);

  // escape-layer 마커는 panel이 보일 때(anchor 있음)만, 그리고 실제로 소비하는 제스처만 인코딩해 단다.
  // - anchor=null(아무것도 안 그림) 동안 마커가 있으면 모달이 안 보이는 popup에 실드를 양보한다 → 제외.
  // - closeOnEscape/closeOnOutsidePointerDown이 꺼진 제스처는 토큰에서 빠져, 모달이 그 제스처를 실드하지
  //   않고 스스로 닫는다(opt-out popup이 모달까지 막는 dead gesture 방지). 둘 다 꺼지면 값이 ""라 마커를 뗀다.
  useEffect(() => {
    if (!portalEl) return;
    const value = anchor
      ? escapeLayerValue({ escape: closeOnEscape, pointer: closeOnOutsidePointerDown })
      : "";
    if (value) portalEl.setAttribute(ESCAPE_LAYER_ATTR, value);
    else portalEl.removeAttribute(ESCAPE_LAYER_ATTR);
  }, [portalEl, anchor, closeOnEscape, closeOnOutsidePointerDown]);

  useLayoutEffect(() => {
    if (!open || !portalEl) return;
    updatePosition();
  }, [
    open,
    portalEl,
    updatePosition,
    children,
    width,
    maxWidth,
    maxHeight,
    style,
    owner?.position,
  ]);

  useLayoutEffect(() => {
    if (!open || !portalEl) return;
    focusInitial();
  }, [open, portalEl, focusInitial]);

  usePopoverDismiss({
    open,
    refs: dismissRefs,
    onClose,
    closeOnEscape: closeOnEscape && !descendants.some((entry) => entry.consumesEscape),
    closeOnOutsidePointerDown,
  });

  const boundaryPortal = bounded ? portalEl : null;
  // 회피 대상의 fixed 좌표/가시성 변경도 추적한다. 대상은 자신을 회피하지 않아야 순환하지 않는다.
  useEffect(() => {
    if (!open || !avoidElements?.length) return;
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updatePosition();
      });
    };
    const mutation = new MutationObserver(schedule);
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    for (const element of avoidElements) {
      mutation.observe(element, { attributes: true, attributeFilter: ["style", "class"] });
      resize?.observe(element);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      mutation.disconnect();
      resize?.disconnect();
    };
  }, [open, avoidElements, updatePosition]);
  const anchorEl = anchorElementRef.current;
  useAnchorHiddenClose({ open, enabled: closeOnAnchorHidden, anchorEl, onClose });
  useAnchoredReposition({
    open: open && (!bounded || Boolean(boundaryPortal)),
    anchorEl,
    matchAnchorWidth,
    boundary: boundaryElement,
    observeClippingAncestors: boundary === "clippingAncestors",
    panel: bounded ? panelRef.current : null,
    repositionOnScroll,
    repositionOnResize,
    updatePosition,
  });

  if (!open || !anchor || !portalEl) return null;

  return createPortal(
    <Layer>
      <Panel
        ref={panelRef}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        className={className}
        data-placement={position.placement}
        style={{ ...style, left: position.left, top: position.top }}
        $width={matchAnchorWidth ? undefined : width}
        $matchAnchorWidth={matchAnchorWidth}
        $bounded={bounded}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
      >
        <PopupOwnerContext.Provider value={descendantContext}>
          {children}
        </PopupOwnerContext.Provider>
      </Panel>
    </Layer>,
    portalEl,
  );
};

export const AnchoredPopupHeader = forwardRef<HTMLDivElement, AnchoredPopupHeaderProps>(
  ({ children, padding = "sm", divider = true, ...rest }, ref) => (
    <HeaderRoot ref={ref} $padding={padding} $divider={divider} {...rest}>
      {children}
    </HeaderRoot>
  ),
);

AnchoredPopupHeader.displayName = "AnchoredPopupHeader";

export const AnchoredPopupBody = forwardRef<HTMLDivElement, AnchoredPopupBodyProps>(
  ({ children, padding = "none", scroll = true, ...rest }, ref) => (
    <BodyRoot ref={ref} $padding={padding} $scroll={scroll} {...rest}>
      {children}
    </BodyRoot>
  ),
);

AnchoredPopupBody.displayName = "AnchoredPopupBody";

export const AnchoredPopupFooter = forwardRef<HTMLDivElement, AnchoredPopupFooterProps>(
  ({ children, padding = "sm", divider = true, align = "end", ...rest }, ref) => (
    <FooterRoot ref={ref} $align={align} $padding={padding} $divider={divider} {...rest}>
      {children}
    </FooterRoot>
  ),
);

AnchoredPopupFooter.displayName = "AnchoredPopupFooter";

export type AnchoredPopupComponent = ((props: AnchoredPopupProps) => ReactElement | null) & {
  Header: typeof AnchoredPopupHeader;
  Body: typeof AnchoredPopupBody;
  Footer: typeof AnchoredPopupFooter;
};

export const AnchoredPopup = Object.assign(AnchoredPopupRoot, {
  Header: AnchoredPopupHeader,
  Body: AnchoredPopupBody,
  Footer: AnchoredPopupFooter,
}) as AnchoredPopupComponent;
